import { Inject } from '@odinjs/odin';
import { DeckAPI, FetchAPI, FilterAPI, FormAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnBeforeFetch, OnBeforeSubmit, OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { DataContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { FetchEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import moment from 'moment';

import { ApplicationRoute } from '../../../../constants/routes';
import { EletronicPrescriptionsUtils } from '../../../../utils/eletronic-prescription-utils';
import BaseController from '../../../BaseController';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { MedicationScheduleParams } from '../schedule-medications-and-solutions/schedule-medications-and-solutions-params';
import { MedicineAndSolutionsParams } from './medicine-and-solutions-params';

import './medicine-and-solutions-controller.css';

@Controller({ component: 'acm/sn-tab-layout', fragment: 'medicine-and-solutions' })
class MedicineAndSolutionsController extends BaseController {

  @Inject('deck-medicine-and-solutions')
  deck!: DeckAPI;

  redirectScheduleMedications(formApi: FormAPI): void {
    const redirectRoute = ApplicationRoute.ADEP_SCHEDULE_MEDICATIONS;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    const value = formApi.getData();

    let scheduleTypeId = value.ieTipoItem;

    if (String(value?.ieTipoItem).includes('Me')) {
      scheduleTypeId = 'Medications';
    } else if (String(value?.ieTipoItem).includes('So')) {
      scheduleTypeId = 'Solutions';
    }

    MedicineAndSolutionsParams.params = {
      cdIntervalId: String(value.intervalId),
      cdMaterialId: String(value.materialId),
      dsMaterialId: String(value.materialDescription),
      encounterId: String(encounterId),
      ieAcmId: String(value.itemType),
      ieSpecialTimeId: String(value.specialTime),
      ieTypeItem: String(value.ieTipoItem),
      ieViaApplicationDescription: String(value.administrationRouteDescription),
      ieViaApplicationId: String(value.administrationRoute),
      nrCurrentStageId: String(value.currentStep),
      nrGroupingId: String(value.groupingId),
      nrPrescriptionId: String(value.medicalPrescriptionId),
      nrSequenceId: String(value.materialPrescriptionId),
      patientId: String(patientId),
      qtDoseId: String(value.doseQty),
    }

    return applicationContext.redirect(redirectRoute, {
      encounterId,
      patientId,
      scheduleTypeId,
    });
  }

  showToast(messge: string, typeMessage: ToastLevel): void {
    ToastService.open({
      dismissExpression: MessageService.translate('tws-ok.ok-text'),
      dismissible: true,
      highlight: false,
      id: 'schedule-medications',
      level: typeMessage,
      message: messge,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  scheduleMedicationsMessage(): void {
    if (MedicationScheduleParams.paramError) {
      switch (MedicationScheduleParams.paramError.type) {
        case 'success':
          this.showToast(MessageService.translate('text.item-scheduled-successfully').replace('.', '!'), ToastLevel.SUCCESS);
          MedicationScheduleParams.paramError = null
          break;
        case 'alert':
          this.showToast(String(MedicationScheduleParams.paramError.message), ToastLevel.WARNING);
          this.showToast(MessageService.translate('text.item-scheduled-successfully'), ToastLevel.SUCCESS);
          MedicationScheduleParams.paramError = null
          break;
        case 'error':
          this.showToast(String(MedicationScheduleParams.paramError.message), ToastLevel.ERROR);
          MedicationScheduleParams.paramError = null
          break;
        default:
          MedicationScheduleParams.paramError = null
      }
    }
  }

  @OnMount({ target: 'acm/sn-tab-layout' })
  onMedicineAndSolutionsLayoutMount(): void {
    InformationBarUtils.collapsedBar = true;

    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    this.context.set({
      encounterId,
      patientId,
    });

    this.scheduleMedicationsMessage();
  }

  @OnMount({ target: 'filter-medicine-and-solutions' })
  onFilterMount(evt: MountEvent): void {
    const filter = evt.target as FilterAPI;
    const data = EletronicPrescriptionsUtils.getFilterInitialData(filter, this.getParent());

    filter.clear();
    filter.setData(data);
  }

  @OnBeforeSubmit({ target: 'filter-medicine-and-solutions' })
  onBeforeFilterSubmit(evt: SubmitEvent): void {
    const record = evt.record;
    this.getParent().set({filterRecord: record});
  }

  @OnBeforeFetch({ target: 'fetch-medicine-and-solutions' })
  onBeforeMedicineAndSolutionsFetch(evt: FetchEvent): void {
    const fetch = evt.target as FetchAPI;
    if (evt) {
      const date = moment().format(ISO_DATE_FORMAT);
      const currentParams = fetch.getParameters();
      const treatedParams = { ...currentParams, date }
      fetch.setParameters(treatedParams);
    }
  }

  @OnAfterFetch({ target: 'fetch-medicine-and-solutions' })
  onAfterMedicineAndSolutionsFetch(evt: FetchEvent): void {
    const response = evt.data as Record<string, Value>[];
    if(response){
      const treatedData = response.map(this.treatItemData);
      this.deck.setData(treatedData);
    }
  }

  @OnClick({ target: 'deck-medicine-and-solutions' })
  onClick(event: FormAPI): void {
    this.redirectScheduleMedications(event);
  }

  getParent(): DataContext {
    interface HasParent { parent: DataContext }
    const contextWithParent = this.context as unknown as HasParent;
    return contextWithParent.parent;
  }

  treatItemData(item: Record<string, Value>): Record<string, Value> {
    const solutionsTitle = MessageService.translate('tws.medicine_and_solutions-solution_title');
    const medicineTitle = MessageService.translate('tws.medicine_and_solutions-medicine_title');

    const ieTipoItem = item.ieTipoItem === 'S' ? solutionsTitle : medicineTitle;
    return {...item, ieTipoItem};
  }
}

export default MedicineAndSolutionsController;